import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { FaGlobe, FaClock, FaPaperPlane, FaEnvelope } from 'react-icons/fa';
import { getPublicDemoRequestUrl } from '../../utils/publicApiBaseUrl';

const EMPTY_FORM = {
  fullName: '',
  email: '',
  phone: '',
  companyName: '',
  industrySector: '',
  companySize: '',
  activeProjects: '',
  planInterest: '',
  primaryOperations: [],
  country: '',
  message: '',
  contactMethod: 'email',
};

const ContactSection = () => {
  const { t } = useLanguage();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(() => ({ ...EMPTY_FORM }));
  const [status, setStatus] = useState(null); // 'sending' | 'success' | 'error'
  const [opsError, setOpsError] = useState(false);

  useEffect(() => {
    const handleOpen = () => setShowForm(true);
    window.addEventListener('openContactForm', handleOpen);
    return () => window.removeEventListener('openContactForm', handleOpen);
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setFormData((prev) => ({
        ...prev,
        primaryOperations: checked
          ? [...prev.primaryOperations, value]
          : prev.primaryOperations.filter((op) => op !== value),
      }));
      setOpsError(false);
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.primaryOperations.length === 0) {
      setOpsError(true);
      return;
    }
    setOpsError(false);
    setStatus('sending');

    try {
      const res = await fetch(getPublicDemoRequestUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          companyName: formData.companyName,
          industrySector: formData.industrySector,
          companySize: formData.companySize,
          activeProjects: formData.activeProjects,
          planInterest: formData.planInterest,
          primaryOperations: formData.primaryOperations,
          country: formData.country,
          message: formData.message,
          contactMethod: formData.contactMethod,
        }),
      });

      if (res.ok) {
        setStatus('success');
        setFormData({ ...EMPTY_FORM });
      } else {
        try {
          const errBody = await res.json();
          console.error('Contact form rejected:', res.status, errBody);
        } catch {
          console.error('Contact form rejected:', res.status);
        }
        setStatus('error');
      }
    } catch (err) {
      console.error('Contact form request failed:', err);
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="landing-section landing-section-dark">
      <div className="container-landing">
        <div className="text-center">
          <h2 className="section-title">{t('contact.title')}</h2>
          <p className="section-subtitle">{t('contact.subtitle')}</p>
        </div>

        {!showForm && status !== 'success' ? (
          <div className="text-center" style={{ marginTop: '2.5rem' }}>
            <div className="contact-info-row">
              <div className="contact-info-item">
                <FaGlobe className="contact-info-icon" />
                <div>
                  <h4>{t('contact.info.coverageTitle')}</h4>
                  <p>{t('contact.info.coverageBody')}</p>
                </div>
              </div>
              <div className="contact-info-item">
                <FaClock className="contact-info-icon" />
                <div>
                  <h4>{t('contact.info.responseTitle')}</h4>
                  <p>{t('contact.info.response')}</p>
                </div>
              </div>
            </div>
            <button
              type="button"
              className="contact-cta-btn"
              onClick={() => setShowForm(true)}
            >
              <FaEnvelope style={{ marginRight: '0.5rem' }} />
              {t('contact.title')}
            </button>
          </div>
        ) : (
          <div className="contact-grid">
            <div className="contact-form-card">
              {status === 'success' ? (
                <div className="contact-success">
                  <FaPaperPlane size={48} style={{ color: '#2ecc71', marginBottom: '1rem' }} />
                  <p>{t('contact.success')}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} noValidate>
                  <h4 className="contact-form-subsection">{t('contactSales.sections.contactInfo')}</h4>
                  <div className="contact-form-row-2">
                    <div className="contact-form-group">
                      <label htmlFor="landing-contact-fullName">{t('contactSales.fields.fullName')} *</label>
                      <input
                        id="landing-contact-fullName"
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        placeholder={t('contactSales.placeholders.fullName')}
                        required
                        autoComplete="name"
                      />
                    </div>
                    <div className="contact-form-group">
                      <label htmlFor="landing-contact-email">{t('contactSales.fields.email')} *</label>
                      <input
                        id="landing-contact-email"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder={t('contactSales.placeholders.email')}
                        required
                        autoComplete="email"
                      />
                    </div>
                  </div>
                  <div className="contact-form-row-2">
                    <div className="contact-form-group">
                      <label htmlFor="landing-contact-phone">{t('contactSales.fields.phone')} *</label>
                      <input
                        id="landing-contact-phone"
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder={t('contactSales.placeholders.phone')}
                        required
                        autoComplete="tel"
                      />
                    </div>
                    <div className="contact-form-group">
                      <label htmlFor="landing-contact-company">{t('contactSales.fields.companyName')} *</label>
                      <input
                        id="landing-contact-company"
                        type="text"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleChange}
                        placeholder={t('contactSales.placeholders.companyName')}
                        required
                        autoComplete="organization"
                      />
                    </div>
                  </div>
                  <div className="contact-form-row-2">
                    <div className="contact-form-group">
                      <label htmlFor="landing-contact-country">{t('contactSales.fields.country')} *</label>
                      <input
                        id="landing-contact-country"
                        type="text"
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        placeholder={t('contactSales.placeholders.country')}
                        required
                        autoComplete="country-name"
                      />
                    </div>
                  </div>

                  <h4 className="contact-form-subsection">{t('contactSales.sections.businessInfo')}</h4>
                  <div className="contact-form-row-2">
                    <div className="contact-form-group">
                      <label htmlFor="landing-contact-industry">{t('contactSales.fields.industrySector')} *</label>
                      <select
                        id="landing-contact-industry"
                        name="industrySector"
                        value={formData.industrySector}
                        onChange={handleChange}
                        required
                      >
                        <option value="">{t('contactSales.placeholders.select')}</option>
                        <option value="seismic">Seismic Exploration</option>
                        <option value="oilgas">Oil &amp; Gas Services</option>
                        <option value="geophysical">Geophysical Contractor</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div className="contact-form-group">
                      <label htmlFor="landing-contact-size">{t('contactSales.fields.companySize')} *</label>
                      <select
                        id="landing-contact-size"
                        name="companySize"
                        value={formData.companySize}
                        onChange={handleChange}
                        required
                      >
                        <option value="">{t('contactSales.placeholders.select')}</option>
                        <option value="1-50">1-50 employees</option>
                        <option value="51-200">51-200 employees</option>
                        <option value="201-500">201-500 employees</option>
                        <option value="500+">500+ employees</option>
                      </select>
                    </div>
                  </div>
                  <div className="contact-form-group">
                    <label htmlFor="landing-contact-active">{t('contactSales.fields.activeProjects')}</label>
                    <select
                      id="landing-contact-active"
                      name="activeProjects"
                      value={formData.activeProjects}
                      onChange={handleChange}
                    >
                      <option value="">{t('contactSales.placeholders.select')}</option>
                      <option value="1-3">1-3 projects</option>
                      <option value="4-10">4-10 projects</option>
                      <option value="11-20">11-20 projects</option>
                      <option value="20+">20+ projects</option>
                    </select>
                  </div>

                  <h4 className="contact-form-subsection">{t('contactSales.sections.productInterest')}</h4>
                  <div className="contact-form-group">
                    <label htmlFor="landing-contact-plan">{t('contactSales.fields.planInterest')} *</label>
                    <select
                      id="landing-contact-plan"
                      name="planInterest"
                      value={formData.planInterest}
                      onChange={handleChange}
                      required
                    >
                      <option value="">{t('contactSales.placeholders.select')}</option>
                      <option value="Starter">Starter</option>
                      <option value="Professional">Professional</option>
                      <option value="Enterprise">Enterprise</option>
                      <option value="not-sure">Not sure yet</option>
                    </select>
                  </div>
                  <div className="contact-form-group">
                    <span className="contact-form-label-block">{t('contactSales.fields.primaryOperations')} *</span>
                    <div className="contact-form-checks">
                      <label>
                        <input
                          type="checkbox"
                          value="survey"
                          checked={formData.primaryOperations.includes('survey')}
                          onChange={handleChange}
                        />
                        Survey Operations
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          value="drilling"
                          checked={formData.primaryOperations.includes('drilling')}
                          onChange={handleChange}
                        />
                        Drilling Operations
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          value="recording"
                          checked={formData.primaryOperations.includes('recording')}
                          onChange={handleChange}
                        />
                        Recording Operations
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          value="pmo"
                          checked={formData.primaryOperations.includes('pmo')}
                          onChange={handleChange}
                        />
                        Project Management Office
                      </label>
                    </div>
                    {opsError && (
                      <p className="contact-error contact-error-inline">{t('contactSales.validation.selectAtLeastOne')}</p>
                    )}
                  </div>

                  <h4 className="contact-form-subsection">{t('contactSales.sections.additionalDetails')}</h4>
                  <div className="contact-form-group">
                    <label htmlFor="landing-contact-message">{t('contactSales.fields.message')}</label>
                    <textarea
                      id="landing-contact-message"
                      name="message"
                      rows={4}
                      value={formData.message}
                      onChange={handleChange}
                      placeholder={t('contactSales.placeholders.message')}
                    />
                  </div>
                  <div className="contact-form-group">
                    <span className="contact-form-label-block">{t('contactSales.fields.contactMethod')}</span>
                    <div className="contact-form-radios">
                      <label>
                        <input
                          type="radio"
                          name="contactMethod"
                          value="email"
                          checked={formData.contactMethod === 'email'}
                          onChange={handleChange}
                        />
                        Email
                      </label>
                      <label>
                        <input
                          type="radio"
                          name="contactMethod"
                          value="phone"
                          checked={formData.contactMethod === 'phone'}
                          onChange={handleChange}
                        />
                        Phone Call
                      </label>
                      <label>
                        <input
                          type="radio"
                          name="contactMethod"
                          value="video"
                          checked={formData.contactMethod === 'video'}
                          onChange={handleChange}
                        />
                        Video Conference
                      </label>
                    </div>
                  </div>

                  {status === 'error' && (
                    <p className="contact-error">{t('contact.error')}</p>
                  )}
                  <button
                    type="submit"
                    className="contact-submit-btn"
                    disabled={status === 'sending'}
                  >
                    {status === 'sending' ? t('contact.form.sending') : t('contactSales.buttons.submit')}
                  </button>
                </form>
              )}
            </div>

            <div className="contact-info-card">
              <div className="contact-info-item">
                <FaGlobe className="contact-info-icon" />
                <div>
                  <h4>{t('contact.info.coverageTitle')}</h4>
                  <p>{t('contact.info.coverageBody')}</p>
                </div>
              </div>
              <div className="contact-info-item">
                <FaClock className="contact-info-icon" />
                <div>
                  <h4>{t('contact.info.responseTitle')}</h4>
                  <p>{t('contact.info.response')}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ContactSection;
